// Copyright 2025, University of Maryland, All Rights Reserved
/**
 * Pipeline Node Instance API Service
 *
 * Handles CRUD operations for individual pipeline node instances using the
 * /pipelines/{pipeline_id}/nodes/{instance_id} endpoints.
 */

import type { NodeApiClient } from '@/app-shell/services'

/**
 * Chunk payload for writing chunked node data.
 */
export interface ChunkPayload {
  chunkId: string
  index: number
  payload: Record<string, unknown>
  sourceFile?: string | null
  dataSourceId?: string | null
  sizeBytes?: number | null
  maxSizeBytes?: number | null
}

/**
 * Request body for updating a node instance.
 */
export interface PutNodeInstanceRequest {
  label?: string | null
  enabled?: boolean
  settings?: Record<string, unknown>
  data?: Record<string, unknown> | null
}

/**
 * Request body for updating node instance data with optional chunks.
 */
export interface PutNodeInstanceDataRequest {
  label?: string | null
  enabled?: boolean
  settings?: Record<string, unknown>
  data?: Record<string, unknown> | null
  sourceFiles?: string[]
  chunks?: ChunkPayload[]
}

/**
 * Node instance envelope from backend.
 */
export interface NodeInstanceEnvelope {
  nodeDefinition: string
  instanceId: string
  label?: string | null
  enabled: boolean
  settings: Record<string, unknown>
  settingsType?: string | null
  runtime: Record<string, unknown>
  payload: Record<string, unknown>
}

/**
 * Chunk summary for node instance data.
 */
export interface NodeInstanceDataChunk {
  chunkId?: string | null
  index?: number | null
  sizeBytes?: number | null
  sourceFile?: string | null
}

/**
 * Response from GET /pipelines/{pipeline_id}/nodes/{instance_id}
 */
export interface GetNodeInstanceResponse {
  instance: NodeInstanceEnvelope
}

/**
 * Response from PUT /pipelines/{pipeline_id}/nodes/{instance_id}
 */
export interface PutNodeInstanceResponse {
  instance: NodeInstanceEnvelope
}

/**
 * Response from PUT /pipelines/{pipeline_id}/nodes/{instance_id}/data
 */
export interface PutNodeInstanceDataResponse {
  instance: NodeInstanceEnvelope
  payload: Record<string, unknown>
  chunkSummary: {
    chunkCount: number
    chunks: NodeInstanceDataChunk[]
  }
}

/** Scope options for node instance calls. */
export interface NodeInstanceScope {
  /** Owning agent — routes the call to the agent's pipeline partition. */
  agentId?: string
}

/** Path to a node instance — the agent route when agentId is set, else the workspace route. */
export function nodeInstancePath(
  orgId: string,
  workspaceId: string,
  pipelineId: string,
  instanceId: string,
  scope?: NodeInstanceScope,
): string {
  if (scope?.agentId) {
    return `/org/${orgId}/ws/${workspaceId}/agents/${encodeURIComponent(scope.agentId)}/pipeline/nodes/${encodeURIComponent(instanceId)}`
  }
  return `/workspaces/${orgId}/${workspaceId}/pipelines/${encodeURIComponent(pipelineId)}/nodes/${encodeURIComponent(instanceId)}`
}

/**
 * Service for node instance CRUD operations.
 */
export class NodeInstanceService {
  constructor(private readonly client: NodeApiClient) {}

  /**
   * Get node instance metadata and settings.
   */
  async getNodeInstance(
    orgId: string,
    workspaceId: string,
    pipelineId: string,
    instanceId: string,
    scope?: NodeInstanceScope
  ): Promise<NodeInstanceEnvelope> {
    const { data } = await this.client.get<GetNodeInstanceResponse>(
      nodeInstancePath(orgId, workspaceId, pipelineId, instanceId, scope)
    )
    return data.instance
  }

  /**
   * Create or update a node instance.
   */
  async putNodeInstance(
    orgId: string,
    workspaceId: string,
    pipelineId: string,
    instanceId: string,
    payload: PutNodeInstanceRequest,
    scope?: NodeInstanceScope
  ): Promise<NodeInstanceEnvelope> {
    const { data } = await this.client.put<PutNodeInstanceResponse>(
      nodeInstancePath(orgId, workspaceId, pipelineId, instanceId, scope),
      payload
    )
    return data.instance
  }

  /**
   * Create or update a node instance with data and optional chunks.
   */
  async putNodeInstanceData(
    orgId: string,
    workspaceId: string,
    pipelineId: string,
    instanceId: string,
    payload: PutNodeInstanceDataRequest
  ): Promise<PutNodeInstanceDataResponse> {
    const { data } = await this.client.put<PutNodeInstanceDataResponse>(
      `/workspaces/${orgId}/${workspaceId}/pipelines/${encodeURIComponent(pipelineId)}/nodes/${encodeURIComponent(instanceId)}/data`,
      payload
    )
    return data
  }
}
